/* eslint-disable @typescript-eslint/no-unused-vars */
import { SocialAuthProvider, SSO_PROVIDERS } from 'constant/sso';
import { SsoService } from '../../services/sso-service';
import { Button } from 'components/ui/button';
import { useConfig } from 'hooks/use-config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SSOSigninCardProps = {
  providerConfig: SocialAuthProvider & {
    audience: string;
    provider: SSO_PROVIDERS;
    isAvailable: boolean;
  };
  showText?: boolean;
  totalProviders?: number;
};

const SSOSigninCard = ({
  providerConfig,
  showText = false,
  totalProviders = 1,
}: SSOSigninCardProps) => {
  const ssoService = new SsoService();
  const { configurationSettings } = useConfig();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { t } = useTranslation();

  // Browser Back from Microsoft/SSO can restore this page via bfcache with isRedirecting still true.
  useEffect(() => {
    const onPageShow = () => setIsRedirecting(false);
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const getButtonWidth = () => {
    if (showText) return 'w-full';

    switch (totalProviders) {
      case 2:
        return 'w-1/2';
      case 3:
        return 'w-1/3';
      case 4:
        return 'w-1/4';
      case 5:
        return 'w-1/5';
      default:
        return 'w-full';
    }
  };

  const onClickHandler = async (e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      if (!providerConfig.isAvailable) {
        return;
      }

      if (!providerConfig?.audience || !providerConfig?.provider) {
        console.error('Missing required provider config:', {
          audience: providerConfig?.audience,
          provider: providerConfig?.provider,
          fullConfig: providerConfig,
        });
        alert(t('PROVIDER_CONFIGURATION_INCOMPLETE', 'Provider configuration is incomplete. Please check the setup.') );
        return;
      }

      setIsRedirecting(true);

      const requestPayload = {
        provider: providerConfig.provider,
        audience: providerConfig.audience,
        sendAsResponse: true,
      };

      const res = await ssoService.getSocialLoginEndpoint(requestPayload, configurationSettings);

      if (res.error) {
        setIsRedirecting(false);
        return alert(`Authentication error: ${res.error}`);
      }

      if (!res?.authorizationUrl) {
        setIsRedirecting(false);
        return alert(t('NO_REDIRECT_URL_RECEIVED', 'No redirect URL received from the authentication service.') );
      }

      localStorage.setItem('sso_provider', providerConfig.provider);
      window.location.href = res.providerUrl;
    } catch (error) {
      console.error('=== UNEXPECTED ERROR ===');
      console.error('Error details:', error);
      console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      setIsRedirecting(false);
      alert(t('UNEXPECTED_ERROR', 'An unexpected error occurred. Please try again.') );
    }
  };

  return (
    <Button
      variant="outline"
      className={`${getButtonWidth()} h-12`}
      onClick={onClickHandler}
      disabled={!providerConfig.isAvailable || isRedirecting}
      data-state={providerConfig.isAvailable ? 'enabled' : 'disabled'}
    >
      {isRedirecting ? (
        t('LOGGING_YOU_IN', 'Logging you in...')
      ) : (
        <>
          <img
            src={providerConfig.imageSrc}
            width={20}
            height={20}
            alt={`${providerConfig.label} logo`}
            className={`${showText ? 'mr-2' : ''} ${!providerConfig.isAvailable ? 'opacity-50' : ''}`}
          />
          {showText && `Log in with ${providerConfig.label}`}
        </>
      )}
    </Button>
  );
};

export default SSOSigninCard;
